import type { LessonPage } from "@tutor/protocol";
import { useEffect, useId, useState, type CSSProperties } from "react";
import { useT } from "../i18n";
import { PromptText } from "./PromptText";

// The lesson written on the board (ADR-0065 point 9). Coach teaches from a reviewed lesson
// card: the board writes that card itself, the idea first, then the worked example one
// step at a time, then the usual trap. The text is the card's, never the model's. Coach's
// line in the conversation only walks the child through it.

/** Time between two lines of the lesson as the board writes them. */
const LINE_MS = 600;

export function LessonBoard(props: { page: LessonPage; animate?: boolean }) {
  const { page, animate = true } = props;
  const t = useT();
  const titleId = useId();
  // A live region announces what changes in it, not what it mounts with: say it just after.
  const [announce, setAnnounce] = useState(false);
  useEffect(() => {
    if (!animate) return;
    const timer = setTimeout(() => {
      setAnnounce(true);
    }, 100);
    return () => {
      clearTimeout(timer);
    };
  }, [animate]);
  // Each line appears after the one before; reduced motion shows them all (styles.css).
  let line = 0;
  const next = (): CSSProperties | undefined =>
    animate ? ({ "--line-delay": `${String(line++ * LINE_MS)}ms` } as CSSProperties) : undefined;
  return (
    <section className="lesson-board" data-animate={animate} aria-labelledby={titleId}>
      <h3 id={titleId} className="lesson-board-title">
        {t("board.lessonPage.title")}
      </h3>
      {animate && (
        <p className="visually-hidden" role="status">
          {announce ? t("board.lessonTeaching.note") : ""}
        </p>
      )}
      <p className="lesson-board-line lesson-board-idea" style={next()}>
        <PromptText text={page.idea} />
      </p>
      <div className="lesson-board-example">
        <p className="lesson-board-line lesson-board-kicker" style={next()}>
          {t("board.lessonPage.example")}
        </p>
        <p className="lesson-board-line lesson-board-prompt" style={next()}>
          <PromptText text={page.example.prompt} />
        </p>
        <ol className="lesson-board-steps">
          {page.example.steps.map((step, index) => (
            <li key={index} className="lesson-board-line" style={next()}>
              <PromptText text={step} />
            </li>
          ))}
        </ol>
        <p className="lesson-board-line lesson-board-result" style={next()}>
          <PromptText text={page.example.result} />
        </p>
      </div>
      <p className="lesson-board-line lesson-board-trap" style={next()}>
        <strong>{t("board.lessonPage.trap")}</strong> <PromptText text={page.trap} />
      </p>
    </section>
  );
}

/** The lesson, folded above the exercise once the child is on it: there to look back at. */
export function LessonAgain(props: { page: LessonPage }) {
  const t = useT();
  return (
    <details className="lesson-again">
      <summary>{t("board.lessonPage.again")}</summary>
      <LessonBoard page={props.page} animate={false} />
    </details>
  );
}
